import { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

import { adminApi } from "services/api";
import { lightDark } from "utils/lightDark";
import {
  PRICING_MODES,
  openLinkPreviewText,
  openLinkPriceLabel,
  flexibleAmountLabel,
} from "utils/flexiblePayment";
import LinkDetailDialog from "./LinkDetailDialog";
import {
  INDIGO_TEXT,
  ROW_LINE,
  CURRENCIES,
  fmt,
  StatusChip,
  ICON_TAP_SX,
  SMALL_BUTTON_TAP_SX,
  selectTapSx,
  fullScreenPaperSx,
} from "./shared";

const OPEN_STATUS = {
  open: { label: "Open", color: "success" },
  paused: { label: "Paused", color: "warning" },
  closed: { label: "Closed", color: "default" },
};

const EMPTY_OPEN_FORM = {
  title: "",
  description: "",
  currency: "GBP",
  pricingMode: "parts",
  amount: "",
  minPayment: "",
  closesAt: "",
};

const todayIso = () => new Date().toISOString().split("T")[0];
const errorText = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

export default function ShareableLinks() {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [links, setLinks] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [form, setForm] = useState(EMPTY_OPEN_FORM);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState(null); // { severity, message }
  const [payersFor, setPayersFor] = useState(null);
  const [payers, setPayers] = useState([]);
  const [payersLoading, setPayersLoading] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [editing, setEditing] = useState(null); // { _id, title, description, closesAt }
  const [deleting, setDeleting] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const loadLinks = useCallback(async () => {
    setFetching(true);
    try {
      const data = await adminApi.getOpenPaymentLinks();
      setLinks(Array.isArray(data) ? data : []);
    } catch (err) {
      setNotice({ severity: "error", message: errorText(err, "Could not load shareable links") });
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const loadPayers = useCallback(async (link) => {
    setPayersLoading(true);
    try {
      setPayers(await adminApi.getOpenPaymentLinkPayers(link._id));
    } catch (err) {
      setNotice({ severity: "error", message: errorText(err, "Could not load payers") });
    } finally {
      setPayersLoading(false);
    }
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  async function handleCreate(e) {
    e.preventDefault();
    setCreating(true);
    setNotice(null);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        currency: form.currency,
        pricingMode: form.pricingMode,
      };
      if (form.pricingMode !== "any") payload.amount = parseFloat(form.amount);
      if (form.pricingMode !== "fixed" && form.minPayment !== "")
        payload.minPayment = parseFloat(form.minPayment);
      if (form.closesAt) payload.closesAt = form.closesAt;
      const created = await adminApi.createOpenPaymentLink(payload);
      setForm(EMPTY_OPEN_FORM);
      setNotice({ severity: "success", message: `Shareable link created: ${created.url}` });
      await loadLinks();
    } catch (err) {
      setNotice({ severity: "error", message: errorText(err, "Failed to create link") });
    } finally {
      setCreating(false);
    }
  }

  async function copyLink(link) {
    try {
      await navigator.clipboard.writeText(link.url);
      setNotice({ severity: "success", message: "Link copied." });
    } catch {
      setNotice({ severity: "info", message: link.url });
    }
  }

  async function togglePaused(link) {
    setBusyId(link._id);
    try {
      await adminApi.updateOpenPaymentLink(link._id, { active: !link.active });
      await loadLinks();
    } catch (err) {
      setNotice({ severity: "error", message: errorText(err, "Update failed") });
    } finally {
      setBusyId(null);
    }
  }

  function openPayers(link) {
    setPayersFor(link);
    setPayers([]);
    loadPayers(link);
  }

  async function saveEdit() {
    setBusyId(editing._id);
    try {
      await adminApi.updateOpenPaymentLink(editing._id, {
        title: editing.title,
        description: editing.description,
        closesAt: editing.closesAt || null,
      });
      setEditing(null);
      await loadLinks();
    } catch (err) {
      setNotice({ severity: "error", message: errorText(err, "Update failed") });
    } finally {
      setBusyId(null);
    }
  }

  async function confirmDelete() {
    const link = deleting;
    setDeleting(null);
    try {
      await adminApi.deleteOpenPaymentLink(link._id);
      setNotice({ severity: "success", message: `Deleted "${link.title}".` });
      await loadLinks();
    } catch (err) {
      setNotice({ severity: "error", message: errorText(err, "Delete failed") });
    }
  }

  const mode = PRICING_MODES[form.pricingMode];
  const preview = openLinkPreviewText(form);

  const tableData = {
    columns: [
      { Header: "Link", accessor: "title", width: "28%", breakAnywhere: true },
      { Header: "Pricing", accessor: "pricing", width: "15%" },
      { Header: "Status", accessor: "status", width: "9%" },
      { Header: "Payers", accessor: "payers", width: "13%" },
      { Header: "Collected", accessor: "collected", width: "11%" },
      { Header: "Actions", accessor: "actions", width: "20%", sortable: false },
    ],
    rows: links.map((link) => ({
      title: (
        <MDBox>
          <MDTypography variant="caption" fontWeight="bold" display="block">
            {link.title}
          </MDTypography>
          <MDTypography
            variant="caption"
            color="text"
            display="block"
            sx={{ fontFamily: "monospace", fontSize: "0.65rem", overflowWrap: "anywhere" }}
          >
            {link.url}
          </MDTypography>
        </MDBox>
      ),
      pricing: (
        <MDBox>
          <MDTypography variant="caption" fontWeight="medium" display="block">
            {openLinkPriceLabel(link)}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            {PRICING_MODES[link.pricingMode]?.label}
          </MDTypography>
        </MDBox>
      ),
      status: (
        <Chip
          label={OPEN_STATUS[link.status]?.label || link.status}
          color={OPEN_STATUS[link.status]?.color || "default"}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      ),
      payers: (
        <MDTypography variant="caption" color="text">
          {link.payers} paid · {link.starters} started
        </MDTypography>
      ),
      collected: (
        <MDTypography variant="caption" fontWeight="medium">
          {fmt(link.collected, link.currency)}
        </MDTypography>
      ),
      actions: (
        <MDBox display="flex" gap={{ xs: 1, md: 0.5 }} flexWrap="wrap">
          <Tooltip title="Copy link">
            <IconButton
              size="small"
              onClick={() => copyLink(link)}
              sx={{ color: INDIGO_TEXT, ...ICON_TAP_SX }}
            >
              <Icon>content_copy</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Payers">
            <IconButton
              size="small"
              onClick={() => openPayers(link)}
              sx={{ color: "#0288d1", ...ICON_TAP_SX }}
            >
              <Icon>group</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title={link.active ? "Pause (stops new payers)" : "Resume"}>
            <span>
              <IconButton
                size="small"
                disabled={busyId === link._id}
                onClick={() => togglePaused(link)}
                sx={{ color: lightDark("#e65100", "#ffb74d"), ...ICON_TAP_SX }}
              >
                {busyId === link._id ? (
                  <CircularProgress size={16} />
                ) : (
                  <Icon>{link.active ? "pause_circle" : "play_circle"}</Icon>
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              sx={{ color: INDIGO_TEXT, ...ICON_TAP_SX }}
              onClick={() =>
                setEditing({
                  _id: link._id,
                  title: link.title,
                  description: link.description || "",
                  closesAt: link.closesAt ? String(link.closesAt).slice(0, 10) : "",
                })
              }
            >
              <Icon>edit</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => setDeleting(link)}
              sx={{ color: lightDark("#d32f2f", "#ef5350"), ...ICON_TAP_SX }}
            >
              <Icon>delete</Icon>
            </IconButton>
          </Tooltip>
        </MDBox>
      ),
    })),
  };

  return (
    <>
      {/* ── Create form ── */}
      <Grid item xs={12}>
        <Card>
          <MDBox p={{ xs: 2, md: 3 }}>
            <MDTypography variant="h5" fontWeight="bold" gutterBottom>
              Create Shareable Link
            </MDTypography>
            <MDTypography variant="body2" color="text" mb={3}>
              One link you can post anywhere. Each person who pays enters their own details and gets
              their own record.
            </MDTypography>

            {notice && (
              <Alert
                severity={notice.severity}
                sx={{ mb: 2, overflowWrap: "anywhere" }}
                onClose={() => setNotice(null)}
              >
                {notice.message}
              </Alert>
            )}

            <form onSubmit={handleCreate}>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Title"
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    required
                    fullWidth
                    size="small"
                    placeholder="e.g. Data & AI Bootcamp — October intake"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Currency</InputLabel>
                    <Select
                      name="currency"
                      value={form.currency}
                      label="Currency"
                      onChange={handleChange}
                      sx={selectTapSx}
                    >
                      {CURRENCIES.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Close date (optional)"
                    name="closesAt"
                    type="date"
                    value={form.closesAt}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: todayIso() }}
                  />
                </Grid>
              </Grid>

              <MDTypography
                variant="caption"
                fontWeight="bold"
                color="text"
                display="block"
                mb={0.75}
              >
                Pricing
              </MDTypography>
              <ToggleButtonGroup
                value={form.pricingMode}
                exclusive
                size="small"
                orientation={isPhone ? "vertical" : "horizontal"}
                fullWidth={isPhone}
                onChange={(_, v) => v && setForm((f) => ({ ...f, pricingMode: v }))}
                sx={{ mb: 1 }}
              >
                {Object.entries(PRICING_MODES).map(([value, m]) => (
                  <ToggleButton
                    key={value}
                    value={value}
                    sx={{ px: 3, minHeight: { xs: 40, sm: "auto" } }}
                  >
                    <Icon sx={{ mr: 0.5, fontSize: "1rem" }}>{m.icon}</Icon>
                    {m.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <MDTypography variant="caption" color="text" display="block" mb={2}>
                {mode.help}
              </MDTypography>

              <Grid container spacing={2} mb={1.5}>
                {mode.amountLabel && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={mode.amountLabel}
                      name="amount"
                      type="number"
                      value={form.amount}
                      onChange={handleChange}
                      required
                      fullWidth
                      size="small"
                      inputProps={{ min: 0.01, step: "0.01" }}
                    />
                  </Grid>
                )}
                {form.pricingMode !== "fixed" && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Minimum per payment (optional)"
                      name="minPayment"
                      type="number"
                      value={form.minPayment}
                      onChange={handleChange}
                      fullWidth
                      size="small"
                      inputProps={{ min: 0, step: "0.01" }}
                    />
                  </Grid>
                )}
              </Grid>
              {preview && (
                <MDTypography variant="caption" color="text" display="block" mb={2}>
                  {preview}
                </MDTypography>
              )}

              <MDBox mb={3}>
                <TextField
                  label="Description shown to payers (optional)"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={2}
                />
              </MDBox>

              <MDButton
                type="submit"
                variant="gradient"
                color="info"
                disabled={creating}
                startIcon={
                  creating ? <CircularProgress size={16} color="inherit" /> : <Icon>add_link</Icon>
                }
              >
                {creating ? "Creating…" : "Create Link"}
              </MDButton>
            </form>
          </MDBox>
        </Card>
      </Grid>

      {/* ── Table ── */}
      <Grid item xs={12}>
        <Card>
          <MDBox p={{ xs: 2, md: 3 }}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
              mb={2}
            >
              <MDTypography variant="h6" fontWeight="bold">
                Shareable Links
              </MDTypography>
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                onClick={loadLinks}
                sx={SMALL_BUTTON_TAP_SX}
              >
                <Icon sx={{ mr: 0.5 }}>refresh</Icon>Refresh
              </MDButton>
            </MDBox>
            <Divider sx={{ mb: 2 }} />
            {fetching ? (
              <MDBox display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </MDBox>
            ) : links.length === 0 ? (
              <MDBox textAlign="center" py={4}>
                <Icon sx={{ fontSize: "3rem", color: "#bdbdbd" }}>share</Icon>
                <MDTypography variant="body2" color="text" mt={1}>
                  No shareable links yet.
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox mx={{ xs: 0, md: -1 }}>
                <DataTable
                  table={tableData}
                  isSorted={false}
                  entriesPerPage={{ defaultValue: 10 }}
                  showTotalEntries
                  noEndBorder
                />
              </MDBox>
            )}
          </MDBox>
        </Card>
      </Grid>

      {/* ── Payers ── */}
      <Dialog
        open={!!payersFor}
        onClose={() => setPayersFor(null)}
        maxWidth="md"
        fullWidth
        fullScreen={isPhone}
        PaperProps={{ sx: isPhone ? fullScreenPaperSx : undefined }}
      >
        {payersFor && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <MDBox minWidth={0} sx={{ overflowWrap: "anywhere" }}>
                Payers — {payersFor.title}
              </MDBox>
              <IconButton onClick={() => setPayersFor(null)} size="small" sx={ICON_TAP_SX}>
                <Icon>close</Icon>
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {payersLoading ? (
                <MDBox display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={24} />
                </MDBox>
              ) : payers.length === 0 ? (
                <MDTypography variant="body2" color="text">
                  Nobody has used this link yet.
                </MDTypography>
              ) : (
                payers.map((p) => (
                  <MDBox
                    key={p._id}
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    py={1}
                    flexWrap={{ xs: "wrap", sm: "nowrap" }}
                    role="button"
                    tabIndex={0}
                    onClick={() => setViewRecord(p)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") setViewRecord(p);
                    }}
                    sx={{
                      borderBottom: ROW_LINE,
                      cursor: "pointer",
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <MDBox flex={1} minWidth={0}>
                      <MDTypography variant="caption" fontWeight="bold" display="block">
                        {p.name}
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        color="text"
                        sx={{ overflowWrap: "anywhere" }}
                      >
                        {p.email} · {new Date(p.createdAt).toLocaleDateString("en-GB")}
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="caption" fontWeight="medium">
                      {p.paymentType === "flexible"
                        ? flexibleAmountLabel(p)
                        : fmt(p.amount, p.currency)}
                    </MDTypography>
                    {p.status === "active" ? (
                      <Chip
                        label="Started, not paid"
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.65rem" }}
                      />
                    ) : (
                      <StatusChip status={p.status} />
                    )}
                  </MDBox>
                ))
              )}
            </DialogContent>
            <DialogActions>
              <MDButton size="small" onClick={() => setPayersFor(null)} sx={SMALL_BUTTON_TAP_SX}>
                Close
              </MDButton>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Edit ── */}
      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        {editing && (
          <>
            <DialogTitle>Edit Shareable Link</DialogTitle>
            <DialogContent dividers>
              <MDTypography variant="caption" color="text" display="block" mb={2}>
                The price can't change once a link exists — create a new link for a new price.
              </MDTypography>
              <TextField
                label="Title"
                value={editing.title}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                onChange={(e) => setEditing((ed) => ({ ...ed, title: e.target.value }))}
              />
              <TextField
                label="Description"
                value={editing.description}
                fullWidth
                multiline
                rows={2}
                sx={{ mb: 2 }}
                onChange={(e) => setEditing((ed) => ({ ...ed, description: e.target.value }))}
              />
              <TextField
                label="Close date (blank = no close date)"
                type="date"
                value={editing.closesAt}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: todayIso() }}
                onChange={(e) => setEditing((ed) => ({ ...ed, closesAt: e.target.value }))}
              />
            </DialogContent>
            <DialogActions>
              <MDButton onClick={() => setEditing(null)}>Cancel</MDButton>
              <MDButton
                color="info"
                variant="gradient"
                onClick={saveEdit}
                disabled={busyId === editing._id || !editing.title.trim()}
              >
                Save
              </MDButton>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Delete ── */}
      <Dialog open={!!deleting} onClose={() => setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Shareable Link?</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">
            This removes the link and any attempts nobody paid. Links people have paid through can't
            be deleted. Pause them instead.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setDeleting(null)}>Cancel</MDButton>
          <MDButton color="error" variant="gradient" onClick={confirmDelete}>
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>

      <LinkDetailDialog
        record={viewRecord}
        onClose={() => setViewRecord(null)}
        onRecordChange={setViewRecord}
        onNotice={(severity, message) => setNotice({ severity, message })}
        onListChanged={() => {
          loadLinks();
          if (payersFor) loadPayers(payersFor);
        }}
      />
    </>
  );
}
